import { debug } from '@gebruederheitz/debuggable';
import { createManualResolver } from '../util/manual-resolver';
/**
 * The localStorage key under which Memberstack stores the member token. It is
 * intentionally the only place in the codebase aware of this Memberstack
 * implementation detail.
 */
const TOKEN_STORAGE_KEY = '_ms-mid';
/**
 * Waits for the Memberstack DOM package to become ready and resolves the
 * current member token.
 *
 * Exported for backwards compatibility — consumers (e.g. the navigation
 * widget) rely on it directly. New code should prefer {@link
 * MemberstackAuthProvider}.
 */
export function awaitMemberstack(debugInstance = null) {
    const [memberstackReady, setMemberstackReady] = createManualResolver();
    const tokenPromise = new Promise((res) => {
        // wait for at most 10 seconds for memberstack to load
        const timeout = debug.timeout(10000);
        void Promise.race([memberstackReady, timeout]).then(() => {
            res(window.$memberstackDom?.getMemberCookie() || '');
        });
    });
    if (!window.$memberstackReady) {
        debugInstance?.log('Memberstack not ready yet.');
        document.addEventListener('memberstack.ready', () => {
            debugInstance?.log('Memberstack now ready after wait');
            setMemberstackReady();
        });
    }
    else {
        debugInstance?.log('Memberstack ready.');
        setMemberstackReady();
    }
    return { memberstackReady, tokenPromise };
}
/**
 * Ask Memberstack to re-fetch the current member. Kept as a standalone
 * function so the single Memberstack API call has one home.
 */
export async function refreshMemberstack() {
    await window.$memberstackDom?.refetch();
}
/**
 * {@link AuthProvider} backed by Memberstack. This is the only class that knows
 * how tokens are obtained, stored and invalidated for the Memberstack-hosted
 * setup.
 */
export class MemberstackAuthProvider {
    // Memberstack authenticates via a bearer token, so the default fetch
    // credentials mode is fine (no cookies to include).
    credentials = undefined;
    readyPromise;
    tokenPromise;
    constructor(debugInstance = null) {
        const { memberstackReady, tokenPromise } = awaitMemberstack(debugInstance);
        this.readyPromise = memberstackReady;
        this.tokenPromise = tokenPromise;
    }
    ready() {
        return this.readyPromise;
    }
    getToken() {
        return this.tokenPromise;
    }
    setToken(token) {
        window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    }
    onExpired() {
        // @HACK:
        //    Memberstack doesn't seem to automatically remove expired tokens,
        //    which is an issue for us. As they don't expose an API method to
        //    this purpose either, we have to resort to deleting the stored
        //    token straight from localStorage.
        window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
    refresh() {
        return refreshMemberstack();
    }
}
//# sourceMappingURL=memberstack-auth-provider.js.map